import { AlertTriangle, Check, ChevronsUpDown } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContentNoPortal, PopoverTrigger } from './Popover';
import { Button } from './Button';
import { Command, CommandEmpty, CommandInput, CommandItem } from './Command';
import { cn, isPresent } from '@ais-ui/utils';
import { InfiniteScroll } from './InfiniteScroll';
import { calculateSelectPopoverHeight } from './calculateSelectPopoverHeight';
import { TruncatedText } from './TruncatedText';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';

export interface SelectData {
  id: string;
  name: string;
}

export interface GroupSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  data: SelectData[];
  placeholder: string;
  className?: string; // Optional prop for additional styling
  notFoundText?: string; // Optional text to show when value exists but item not found
  onNotFound?: (value: string) => void; // Optional callback when value not found in data
  isLoading?: boolean; // Optional flag to suppress onNotFound while data is loading
  'aria-invalid'?: React.ComponentProps<'button'>['aria-invalid'];
  'aria-describedby'?: string;
}

const filterByName = (entry: SelectData, name: string): SelectData | null => {
  if (entry.name?.toLowerCase().includes(name)) {
    return entry;
  }

  return null;
};

const filterData = (data: SelectData[], name: string) => {
  const lowercaseName = name.toLowerCase();
  return data
    .map((entry) => filterByName(entry, lowercaseName))
    .filter(isPresent);
};

export function SelectWithSearch({
  value,
  onChange,
  data,
  placeholder,
  className,
  notFoundText,
  onNotFound,
  isLoading,
  ...props
}: GroupSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredData = data != null ? filterData(data, search) : data;
  const selectedItem = value ? data?.find((entry) => entry.id === value) : null;
  const showNotFound = value && !selectedItem && notFoundText;

  const calculatedHeight = useMemo(
    () =>
      calculateSelectPopoverHeight({ itemCount: filteredData?.length ?? 0 }),
    [filteredData?.length],
  );

  // Report not found to callback when value exists but item not in data (and data is fully loaded)
  useEffect(() => {
    if (!isLoading && value && !selectedItem && data?.length > 0 && onNotFound) {
      onNotFound(value);
    }
  }, [value, selectedItem, data, onNotFound, isLoading]);

  return (
    <TooltipProvider>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className={cn(
              'w-full justify-between pr-3 text-base shadow-xs font-normal md:text-sm',
              'aria-invalid:shadow-destructive-sm',
              className,
            )}
            {...props}
          >
            {selectedItem ? (
              <TruncatedText text={selectedItem.name} className="text-left" />
            ) : showNotFound ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-normal text-destructive flex items-center gap-1 cursor-help">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    {notFoundText}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>ID: {value}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="font-normal">{placeholder}</span>
            )}
            <span className="flex-1"></span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContentNoPortal
          align="start"
          className="w-[350px] p-0 overflow-hidden"
          style={{ height: `${calculatedHeight}px` }}
        >
          <Command className="flex flex-col w-full h-full" shouldFilter={false}>
            <CommandInput
              className="w-full"
              placeholder={placeholder}
              onValueChange={setSearch}
              value={search}
            />
            <CommandEmpty>No entries found</CommandEmpty>
            <InfiniteScroll
              items={filteredData}
              limit={30}
              observeResize={true}
              className="flex-1 min-h-0 p-0 max-w-full border"
              renderItem={(entry) => (
                <CommandItem
                  className="w-full"
                  key={entry.id}
                  value={entry.id}
                  onSelect={(currentValue) => {
                    onChange(currentValue === value ? undefined : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-1 size-4 flex-shrink-0',
                      value === entry.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <TruncatedText
                    text={entry.name}
                    maxWidth="300px"
                    className="flex-1"
                  />
                </CommandItem>
              )}
            />
          </Command>
        </PopoverContentNoPortal>
      </Popover>
    </TooltipProvider>
  );
}
